import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { AGGREGATION_TYPE, GRAPH_PERIODICITY } from '../../../../../utils/enums/generic.enum';
import { NodalBasisStyles as styles } from './nodal-basis.styles';
import { NodalBasisTemplate as template } from './nodal-basis.template';
import Highcharts from 'highcharts';
import { IProjectHistoricalTs } from '../../../../../types';
import {
  getDatesFromData,
  getFormattedNumber,
  getValuesFromData,
} from '../../../../../utils/chart-data-utils';

const name = 'nodal-basis';

@customElement({
  name,
  template,
  styles,
})
export class NodalBasis extends FASTElement {
  @observable historicalData: Array<IProjectHistoricalTs>;

  @observable nodalBasisChart!: HTMLDivElement;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;
  @observable isVisible: boolean = false;

  connectedCallback() {
    this.renderNodalBasis();
    super.connectedCallback();
  }

  public renderGraph(historicalData: Array<IProjectHistoricalTs>) {
    this.historicalData = [];
    if (historicalData) {
      // clean historical data for nodal basis graph to not show null values
      const dataStartIndex = historicalData.findIndex((e) => e['BASIS_ASGEN_USD_PER_MWH'] !== null);
      this.historicalData = historicalData.slice(dataStartIndex);
    }
    this.renderNodalBasis();
  }

  public periodicityChanged(oldValue, newValue) {
    this.renderNodalBasis();
  }

  public reflow() {
    this.isVisible = false;
    setTimeout(() => {
      this.renderNodalBasis();
    }, 1);
  }

  private renderNodalBasis() {
    if (!this.historicalData || this.historicalData.length === 0) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;

    const dates = getDatesFromData(this.historicalData, this.periodicity);
    const nodalGenerationWeightedBasis = getValuesFromData(
      this.historicalData,
      'BASIS_ASGEN_USD_PER_MWH',
      this.periodicity,
      this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
    );
    const cumulativeNodalSettlementBasis = getValuesFromData(
      this.historicalData,
      'BASIS_CUMULATIVE_SUM_USD',
      this.periodicity,
      this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
    );

    Highcharts.chart({
      chart: {
        renderTo: this.nodalBasisChart,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: dates,
      },
      yAxis: [
        {
          // Primary yAxis
          title: {
            text: '$/MWh',
          },
          labels: {
            formatter: function () {
              return getFormattedNumber(this.value);
            },
          },
        },
        {
          // Secondary yAxis
          title: {
            text: 'Cash Flows ($)',
            rotation: -90,
            margin: 20,
          },
          labels: {
            formatter: function () {
              return getFormattedNumber(this.value);
            },
          },
          opposite: true,
        },
      ],
      tooltip: {
        formatter: function () {
          return this.points.reduce(function (s, point) {
            return s + '<br/>' + point.series.name + ': ' + getFormattedNumber(point.y);
          }, '<b>' + this.x + '</b>');
        },
        valueDecimals: 2,
      },
      series: [
        {
          type: 'line',
          color: Highcharts.getOptions().colors[6],
          yAxis: 0,
          name: 'Nodal Generation Weighted Basis - Historical ($/MWh)',
          data: nodalGenerationWeightedBasis,
          zIndex: 2,
        },
        {
          type: 'area',
          color: Highcharts.getOptions().colors[2],
          yAxis: 1,
          name: 'Cumulative Nodal Settlement Basis - Historical ($)',
          data: cumulativeNodalSettlementBasis,
          zIndex: 1,
          zoneAxis: 'y',
          zones: [
            {value: 0, color: Highcharts.getOptions().colors[2]},
            {color: Highcharts.getOptions().colors[5]},
          ]
        },
      ],
    });
  }
}
